import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { BalanceInfo, TopUpRequest } from '../../../types/organization.types'

interface OrganizationBalanceState {
  balanceInfo: BalanceInfo | null
  loading: boolean
  error: string | null
  topUpRequests: TopUpRequest[]
  topUpLoading: boolean
  creatingTopUp: boolean
  uploadingProof: boolean
  resettingDemo: boolean
}

const initialState: OrganizationBalanceState = {
  balanceInfo: null,
  loading: false,
  error: null,
  topUpRequests: [],
  topUpLoading: false,
  creatingTopUp: false,
  uploadingProof: false,
  resettingDemo: false,
}

const organizationBalanceSlice = createSlice({
  name: 'organizationBalance',
  initialState,
  reducers: {
    // Fetch balance
    fetchBalanceStart(state) {
      state.loading = true
      state.error = null
    },
    fetchBalanceSuccess(state, action: PayloadAction<BalanceInfo>) {
      state.loading = false
      state.balanceInfo = action.payload
      state.error = null
    },
    fetchBalanceFailure(state, action: PayloadAction<string>) {
      state.loading = false
      state.error = action.payload
    },

    // Create top-up request
    createTopUpStart(state) {
      state.creatingTopUp = true
      state.error = null
    },
    createTopUpSuccess(state, action: PayloadAction<TopUpRequest>) {
      state.creatingTopUp = false
      state.topUpRequests.unshift(action.payload)
      state.error = null
    },
    createTopUpFailure(state, action: PayloadAction<string>) {
      state.creatingTopUp = false
      state.error = action.payload
    },

    // Fetch top-up requests
    fetchTopUpsStart(state) {
      state.topUpLoading = true
      state.error = null
    },
    fetchTopUpsSuccess(state, action: PayloadAction<TopUpRequest[]>) {
      state.topUpLoading = false
      state.topUpRequests = action.payload
      state.error = null
    },
    fetchTopUpsFailure(state, action: PayloadAction<string>) {
      state.topUpLoading = false
      state.error = action.payload
    },

    // Upload proof
    uploadProofStart(state) {
      state.uploadingProof = true
      state.error = null
    },
    uploadProofSuccess(state, action: PayloadAction<TopUpRequest>) {
      state.uploadingProof = false
      const index = state.topUpRequests.findIndex((t) => t.id === action.payload.id)
      if (index !== -1) {
        state.topUpRequests[index] = action.payload
      }
      state.error = null
    },
    uploadProofFailure(state, action: PayloadAction<string>) {
      state.uploadingProof = false
      state.error = action.payload
    },

    // Reset demo balance
    resetDemoBalanceStart(state) {
      state.resettingDemo = true
      state.error = null
    },
    resetDemoBalanceSuccess(state, action: PayloadAction<number>) {
      state.resettingDemo = false
      // API returns balance data directly, not nested under 'balance'
      if (state.balanceInfo) {
        state.balanceInfo.demo_balance = action.payload
      }
      state.error = null
    },
    resetDemoBalanceFailure(state, action: PayloadAction<string>) {
      state.resettingDemo = false
      state.error = action.payload
    },

    // Clear error
    clearError(state) {
      state.error = null
    },

    // Reset state
    resetBalanceState: () => initialState,
  },
})

export const {
  fetchBalanceStart,
  fetchBalanceSuccess,
  fetchBalanceFailure,
  createTopUpStart,
  createTopUpSuccess,
  createTopUpFailure,
  fetchTopUpsStart,
  fetchTopUpsSuccess,
  fetchTopUpsFailure,
  uploadProofStart,
  uploadProofSuccess,
  uploadProofFailure,
  resetDemoBalanceStart,
  resetDemoBalanceSuccess,
  resetDemoBalanceFailure,
  clearError,
  resetBalanceState,
} = organizationBalanceSlice.actions

export default organizationBalanceSlice.reducer
